type JsonValue = string | number | boolean | null | JsonArray | JsonObject | undefined;
type JsonArray = JsonValue[];
type JsonObject = { [key: string]: JsonValue };

interface StyleConfig {
	// Base elements
	container?: string;
	item?: string;
	key?: string;
	value?: string;

	// Value types
	number?: string;
	boolean?: string;
	null?: string;
	string?: string;

	// Structure elements
	heading?: string;
	section?: string;
	content?: string;

	// Lists and arrays
	list?: string;
	listItem?: string;
	arrayItem?: string;
	arrayItemHeader?: string;

	// Special cases
	circular?: string;

	// Nesting levels (1-5)
	level1?: string;
	level2?: string;
	level3?: string;
	level4?: string;
	level5?: string;
}

interface AutoHeaderOptions {
	minDepth?: number;
	maxHeaderLevel?: number;
	includeArrays?: boolean;
}

interface JsonToHtmlOptions {
	headers?: Record<string, number>;
	initialPath?: string;
	useStyles?: "default" | "none" | StyleConfig;
	listObjects?: boolean | string[];
	formatKeys?: boolean;
	styleLocation?: "inline" | "top";
	autoHeaders?: boolean | AutoHeaderOptions;
}

/**
 * Analyzes a JSON structure and returns appropriate header mappings
 */
const generateHeaderMappings = (json: JsonValue, options: AutoHeaderOptions = {}): Record<string, number> => {
	const { minDepth = 0, maxHeaderLevel = 4, includeArrays = true } = options;
	const headers: Record<string, number> = {};

	const analyze = (value: JsonValue, path: string, depth: number) => {
		if (value === null || typeof value !== "object") return;

		if (depth >= minDepth) {
			const headerLevel = Math.min(depth - minDepth + 1, maxHeaderLevel);

			if (Array.isArray(value)) {
				if (includeArrays && value.length > 0) {
					headers[path] = headerLevel;

					value.forEach((item, index) => {
						if (typeof item === "object" && item !== null) {
							const itemPath = path ? `${path}[${index}]` : `[${index}]`;
							analyze(item, itemPath, depth + 1);
						}
					});
				}
			} else if (typeof value === "object") {
				Object.entries(value).forEach(([key, val]) => {
					const newPath = path ? `${path}.${key}` : key;

					if (typeof val === "object" && val !== null) {
						headers[newPath] = headerLevel;
						analyze(val, newPath, depth + 1);
					}
				});
			}
		} else {
			// If below minDepth, continue analyzing without creating headers
			if (Array.isArray(value)) {
				value.forEach((item, index) => {
					const itemPath = path ? `${path}[${index}]` : `[${index}]`;
					analyze(item, itemPath, depth + 1);
				});
			} else if (typeof value === "object" && value !== null) {
				Object.entries(value).forEach(([key, val]) => {
					const newPath = path ? `${path}.${key}` : key;
					analyze(val, newPath, depth + 1);
				});
			}
		}
	};

	analyze(json, "", 0);
	return headers;
};

/**
 * Converts JSON to HTML with standardized structure
 */
const jsonToHtml = (json: JsonValue, options: JsonToHtmlOptions = {}) => {
	const {
		autoHeaders = false,
		headers: userHeaders = {},
		initialPath = "",
		useStyles = "default",
		listObjects = false,
		formatKeys = false,
		styleLocation = "inline",
	} = options;

	// Merge auto-generated headers with user-provided headers
	const headers = {
		...(autoHeaders ? generateHeaderMappings(json, typeof autoHeaders === "boolean" ? {} : autoHeaders) : {}),
		...userHeaders, // User headers take precedence
	};

	const defaultStyles: StyleConfig = {
		// Base elements
		container: "font-family: system-ui, sans-serif; line-height: 1.5; padding: 0;",
		item: "margin: 4px 0;",
		key: "font-weight: bold; color: #111;",
		value: "color: #444;",

		// Value types
		number: "color: #0F766E;",
		boolean: "color: #9333EA;",
		null: "color: #888; font-style: italic;",
		string: "color: #444;",

		// Structure
		heading: "margin: 16px 0 8px 0; font-size: 1em;",
		section: "margin-bottom: 12px;",
		content: "margin-left: 10px;",

		// Lists and arrays
		list: "margin: 0; padding-left: 20px; line-height: 1.5;",
		listItem: "margin: 2px 0;",
		arrayItem: "border: 1px solid #e5e7eb; border-radius: 6px; padding: 8px; margin-bottom: 8px;",
		arrayItemHeader:
			"background-color: #f9fafb; border-radius: 4px 4px 0 0; font-weight: bold; margin: -8px -8px 8px; padding: 4px 8px; border-bottom: 1px solid #e5e7eb;",

		// Special cases
		circular: "color: #FF0000;",

		// Removed individual level margins as they will naturally accumulate
	};

	const styles =
		useStyles === "none" ? {} : useStyles === "default" ? defaultStyles : { ...defaultStyles, ...useStyles };

	// Apply styles dynamically
	const applyStyle = (key: keyof StyleConfig) => {
		if (!styles[key]) return "";

		const styleString = styles[key] || "";

		// Remove level-specific styling since we're using natural nesting now
		if (styleLocation === "inline") {
			return ` style="${styleString}"`;
		}

		return ` class="json-${key}"`;
	};

	const generateStyleTag = () => {
		if (styleLocation !== "top" || useStyles === "none") return "";

		const cssRules = Object.entries(styles)
			.map(([key, value]) => `.json-${key} { ${value} }`)
			.join("\n");

		// Remove level-specific classes since we're using natural nesting now

		return `<style>\n${cssRules}\n</style>\n\n`;
	};

	const visited = new WeakSet();
	const escapeHtml = (str: string) => {
		return String(str)
			.replace(/&/g, "&amp;")
			.replace(/</g, "&lt;")
			.replace(/>/g, "&gt;")
			.replace(/"/g, "&quot;")
			.replace(/'/g, "&#39;");
	};

	const formatKey = (key: string) => {
		if (!formatKeys) return key;

		return key
			.split("_")
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1).replace(/([A-Z])/g, " $1"))
			.join(" ")
			.replace(/\s+/g, " ")
			.trim();
	};

	const formatPrimitive = (value: JsonValue) => {
		if (value === null) {
			return `<span${applyStyle("null")}>null</span>`;
		}

		switch (typeof value) {
			case "number":
				return `<span${applyStyle("number")}>${value}</span>`;
			case "boolean":
				return `<span${applyStyle("boolean")}>${value}</span>`;
			case "string":
				return `<span${applyStyle("string")}>${escapeHtml(value)}</span>`;
			default:
				return `<span${applyStyle("value")}>${escapeHtml(String(value))}</span>`;
		}
	};

	const shouldBeHeader = (path: string): number | null => {
		if (path.match(/\[\d+\]/)) {
			const arrayPattern = path.replace(/\[\d+\]/g, "[]");
			return headers[arrayPattern] || headers[path] || null;
		}
		return headers[path] || null;
	};

	const isListObject = (path: string): boolean => {
		if (Array.isArray(listObjects)) {
			const lastKey = path.split(".").pop();
			return lastKey ? listObjects.includes(lastKey) : false;
		}
		return !!listObjects;
	};

	// Main conversion functions
	const convertValue = (value: JsonValue, path = "", level = 0): string => {
		if (value === null || typeof value !== "object") {
			return formatPrimitive(value);
		}

		if (visited.has(value)) {
			return `<span${applyStyle("circular")}>Circular Reference</span>`;
		}

		visited.add(value);

		if (Array.isArray(value)) {
			return convertArray(value, path, level);
		}

		return convertObject(value, path, level);
	};

	const convertArray = (arr: JsonArray, path: string, level: number): string => {
		if (arr.length === 0) {
			return `<div${applyStyle("item")}>Empty Array</div>`;
		}

		// Simple arrays (primitives only)
		const isPrimitiveArray = arr.every((item) => typeof item !== "object" || item === null);
		if (isPrimitiveArray) {
			return `<ul${applyStyle("list")}>
        ${arr.map((item) => `<li${applyStyle("listItem")}>${formatPrimitive(item)}</li>`).join("")}
      </ul>`;
		}

		// Single object array (simplified)
		if (arr.length === 1 && typeof arr[0] === "object" && arr[0] !== null) {
			return convertValue(arr[0], `${path}[0]`, level);
		}

		// Multi-object array (with structure)
		return `<div${applyStyle("content")}>
      ${arr
				.map((item, index) => {
					const itemPath = `${path}[${index}]`;
					const headerLevel = shouldBeHeader(itemPath);

					// Standardized array item structure
					return `<div${applyStyle("arrayItem")}>
          <div${applyStyle("arrayItemHeader")}>Item ${index + 1}</div>
          ${
						headerLevel
							? `<h${headerLevel}${applyStyle("heading")}>${formatKey(path.split(".").pop() || "")}</h${headerLevel}>`
							: ""
					}
          ${convertValue(item, itemPath, level + 1)}
        </div>`;
				})
				.join("")}
    </div>`;
	};

	const convertObject = (obj: JsonObject, path: string, level: number): string => {
		const keys = Object.keys(obj);
		if (keys.length === 0) {
			return `<div${applyStyle("item")}>Empty Object</div>`;
		}

		// List-style rendering for configured objects
		if (isListObject(path)) {
			return `<ul${applyStyle("list")}>
        ${keys
					.map((key) => {
						const value = obj[key];
						const newPath = path ? `${path}.${key}` : key;
						return `<li${applyStyle("listItem")}>
            <span${applyStyle("key")}>${escapeHtml(formatKey(key))}</span>: ${convertValue(value, newPath, level + 1)}
          </li>`;
					})
					.join("")}
      </ul>`;
		}

		// Standard object rendering with consistent structure
		return `<div${applyStyle("content")}>
      ${keys
				.map((key) => {
					const value = obj[key];
					const newPath = path ? `${path}.${key}` : key;
					const headerLevel = shouldBeHeader(newPath);

					if (headerLevel) {
						return `<section${applyStyle("section")}>
            <h${headerLevel}${applyStyle("heading")}>${escapeHtml(formatKey(key))}</h${headerLevel}>
            <div${applyStyle("content")}>
              ${convertValue(value, newPath, level + 1)}
            </div>
          </section>`;
					}

					return `<div${applyStyle("item")}>
          <span${applyStyle("key")}>${escapeHtml(formatKey(key))}</span>: 
          ${
						typeof value === "object" && value !== null
							? convertValue(value, newPath, level + 1)
							: convertValue(value, newPath, level)
					}
        </div>`;
				})
				.join("")}
    </div>`;
	};

	// Generate the final HTML with standardized structure
	const html = `${generateStyleTag()}<div${applyStyle("container")}>
    ${convertValue(json, initialPath)}
  </div>`;

	// Clean up any excessive whitespace
	return html
		.replace(/>\s+</g, "><")
		.replace(/\s{2,}/g, " ")
		.trim();
};

export { jsonToHtml };
